/**
 * Recover from a stale bundle after a deploy.
 *
 * Code splitting means the main bundle fetches `static/js/<id>.<hash>.chunk.js`
 * on demand — when a route is opened, not at load. A deploy replaces every
 * hashed filename, so a browser still running the PREVIOUS build (its own
 * cached index.html and main bundle) asks for chunk names that no longer exist
 * and gets a ChunkLoadError the moment the user opens a lazy route. The tab
 * looked fine until then, which is why this surfaces as "the app randomly broke"
 * rather than "the deploy broke it".
 *
 * The fix is to reload, which fetches the current index.html and with it the
 * current bundle. Cheap and invisible: the user is looking at a route that
 * failed to open, so there is nothing to lose.
 *
 * Guarded to ONE reload per session. If the reload lands on a stale index.html
 * anyway — a caching layer we do not control — a second attempt would fail
 * identically, and an unguarded handler would spin the tab in a reload loop
 * that is far worse than the error it replaces. After a successful load the
 * flag clears, so the next deploy can recover the same way.
 *
 * Server-side companion (the actual prevention, not just recovery):
 * `index.html` must be served with `Cache-Control: no-cache` so it revalidates,
 * while `/static/` may cache forever because every filename is content-hashed.
 */
const RELOAD_FLAG = 'smartform:chunk-reloaded';

const CHUNK_ERROR = /ChunkLoadError|Loading chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

export function isChunkError(candidate) {
  if (!candidate) return false;
  const message =
    candidate.message ||
    candidate.reason?.message ||
    candidate.reason ||
    candidate.error?.message ||
    candidate;
  return CHUNK_ERROR.test(String(message));
}

export default function installChunkReload() {
  if (typeof window === 'undefined') return;

  const recover = (event) => {
    if (!isChunkError(event)) return;
    try {
      if (window.sessionStorage.getItem(RELOAD_FLAG)) return;
      window.sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      // Storage can be unavailable (private mode, blocked cookies). Without a
      // guard a reload loop is possible, so do nothing rather than risk one.
      return;
    }
    window.location.reload();
  };

  window.addEventListener('error', recover);
  window.addEventListener('unhandledrejection', recover);

  // The app got far enough to run: whatever went wrong is behind us, so let a
  // future deploy trigger recovery again.
  window.setTimeout(() => {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* nothing to clear */
    }
  }, 10000);
}
